
import React, { useState, useMemo } from 'react';
import { MenuItem, CartItem, OrderSession, Table, Reservation, AppSettings, Order, ReservationSource } from '../types';
import { SOURCE_LABELS } from '../constants';
import { Search, Plus, Minus, ArrowLeft, Users, Calendar, Edit2, Coffee, Sun, Cloud, Umbrella, Monitor, Star, Heart, Music, Leaf, Zap, Moon, AlertCircle, Phone, Store, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwipeNumberInput } from './MobileControls';
import { getJapanTodayStr } from '../App';

interface PosViewProps {
  tables: Table[];
  menuItems: MenuItem[];
  activeSessions: OrderSession[];
  orders: Order[];
  reservations: Reservation[];
  appSettings: AppSettings;
  onCreateSession: (tableId: string, customerName: string, guests: number, notes?: string) => void;
  onUpdateSession: (sessionId: string, items: CartItem[], total: number) => void;
  onUpdateSessionInfo: (sessionId: string, customerName: string, guests: number) => void;
  onDeleteSession: (sessionId: string) => void;
  onCheckoutSession: (session: OrderSession) => void;
  onAddReservation: (res: Reservation) => void;
}

const ICON_MAP: Record<string, React.ElementType> = {
  coffee: Coffee, sun: Sun, cloud: Cloud, moon: Moon, umbrella: Umbrella, leaf: Leaf, users: Users, monitor: Monitor, music: Music, star: Star, heart: Heart, zap: Zap,
};

const CustomInstagramIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const CustomLineIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
  </svg>
);

const SourceIcon: React.FC<{ source: ReservationSource }> = ({ source }) => {
  switch (source) {
    case 'instagram': return <CustomInstagramIcon size={16} />;
    case 'line': return <CustomLineIcon size={16} />;
    case 'phone': return <Phone size={16} />;
    case 'store': return <Store size={16} />;
    default: return <Store size={16} />;
  }
};

export const PosView: React.FC<PosViewProps> = ({ 
  tables, menuItems, activeSessions, orders, reservations, appSettings, onCreateSession, onUpdateSession, onUpdateSessionInfo, onDeleteSession, onCheckoutSession, onAddReservation
}) => {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [targetTableId, setTargetTableId] = useState<string | null>(null);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newGuests, setNewGuests] = useState(2);
  const [newNotes, setNewNotes] = useState('');
  const [newSource, setNewSource] = useState<ReservationSource>('instagram');
  const [isEditSessionModalOpen, setIsEditSessionModalOpen] = useState(false);
  const [editSessionName, setEditSessionName] = useState('');
  const [editSessionGuests, setEditSessionGuests] = useState(1);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [reservationDate, setReservationDate] = useState(getJapanTodayStr());
  const [reservationHour, setReservationHour] = useState(12);
  const [reservationMinute, setReservationMinute] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const currentSession = activeSessions.find(s => s.id === selectedSessionId);
  const cart = currentSession ? currentSession.items : [];
  const cartTotal = currentSession ? currentSession.total : 0;

  const todayReservations = useMemo(() => {
    const todayStr = getJapanTodayStr();
    return reservations.filter(r => 
      r.date === todayStr && 
      r.status !== 'cancelled' && 
      !activeSessions.some(s => s.customerName === r.name) &&
      !orders.some(o => o.label.includes(r.name))
    );
  }, [reservations, activeSessions, orders]);

  const filteredItems = menuItems.filter(item => item.isActive && item.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleTableClick = (tableId: string) => {
    const existingSession = activeSessions.find(s => s.tableId === tableId);
    if (existingSession) {
      setSelectedSessionId(existingSession.id);
    } else {
      setTargetTableId(tableId);
      setNewCustomerName('');
      setNewGuests(2);
      setNewNotes('');
      setNewSource('instagram');
      setIsCheckInModalOpen(true);
    }
  };

  const handleCheckInSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (targetTableId && newCustomerName.trim()) {
      onCreateSession(targetTableId, newCustomerName, newGuests, newNotes);
      setIsCheckInModalOpen(false);
    }
  };

  const handleReservationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (targetTableId && newCustomerName.trim()) {
      const formattedTime = `${reservationHour.toString().padStart(2, '0')}:${reservationMinute.toString().padStart(2, '0')}`;
      const newReservation: Reservation = {
        id: Date.now().toString(),
        name: newCustomerName,
        date: reservationDate,
        time: formattedTime,
        guests: newGuests,
        source: newSource,
        notes: newNotes,
        status: 'confirmed',
        tableId: targetTableId
      };
      onAddReservation(newReservation);
      setIsReservationModalOpen(false);
    }
  };

  const updateQuantity = (itemId: string, delta: number) => {
    if (!currentSession) return;
    const newCart = cart.map(item => item.id === itemId ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item).filter(item => item.quantity > 0);
    onUpdateSession(currentSession.id, newCart, newCart.reduce((s, i) => s + i.price * i.quantity, 0));
  };

  const addToCart = (item: MenuItem) => {
    if (!currentSession) return;
    const existing = cart.find(i => i.id === item.id);
    const newCart = existing ? cart.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i) : [...cart, { ...item, quantity: 1 }];
    onUpdateSession(currentSession.id, newCart, newCart.reduce((s, i) => s + i.price * i.quantity, 0));
  };

  const handleCheckout = () => {
    if (!currentSession) return;
    setShowConfirmation(true);
    setTimeout(() => {
      onCheckoutSession(currentSession);
      setSelectedSessionId(null);
      setShowConfirmation(false);
    }, 800);
  };

  const renderTable = (table: Table) => {
    const session = activeSessions.find(s => s.tableId === table.id);
    const isActive = !!session;
    const TableIcon = table.icon && ICON_MAP[table.icon] ? ICON_MAP[table.icon] : null;

    if (isActive) {
      return (
        <motion.div
          key={table.id}
          layoutId={`table-${table.id}`}
          onClick={() => handleTableClick(table.id)}
          className="relative rounded-[2.5rem] flex flex-col justify-between text-left transition-all border-2 overflow-hidden shadow-lg h-48 cursor-pointer bg-white border-orange-100"
        >
          <div className="p-4 pb-0 z-10 flex justify-between items-center shrink-0">
            <span className="text-[10px] font-black opacity-30 uppercase tracking-widest">{table.name}</span>
            <span className="w-3 h-3 bg-green-400 rounded-full shadow-[0_0_8px_rgba(74,222,128,0.5)]"></span>
          </div>
          <div className="p-4 py-1 flex flex-col h-full z-10 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center font-bold text-orange-700 overflow-hidden shrink-0 shadow-inner">
                {table.customIcon ? <img src={table.customIcon} className="w-full h-full object-cover" /> : TableIcon ? <TableIcon size={32}/> : table.name.charAt(0)}
              </div>
              <div className="truncate font-black text-lg leading-tight flex-1">{session.customerName} 様</div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-bold text-stone-400">
               <Users size={14}/> {session.guestCount}名
            </div>
            {session.notes && (
              <div className="mt-1 text-[10px] text-stone-400 italic line-clamp-2 border-t border-stone-50 pt-1">
                 {session.notes}
              </div>
            )}
          </div>
          <div className="absolute -bottom-6 -right-6 text-9xl font-black opacity-[0.03] select-none pointer-events-none italic">{table.name}</div>
        </motion.div>
      );
    }

    return (
      <motion.div
        key={table.id}
        layoutId={`table-${table.id}`}
        onClick={() => handleTableClick(table.id)}
        className="relative rounded-[2.5rem] flex flex-col items-center justify-center text-center transition-all border-2 border-dashed border-stone-200 overflow-hidden bg-stone-50/30 hover:bg-stone-50 group h-48 cursor-pointer"
      >
        <button 
          onClick={(e) => { e.stopPropagation(); setTargetTableId(table.id); setNewCustomerName(''); setNewGuests(2); setNewNotes(''); setNewSource('instagram'); setIsReservationModalOpen(true); }} 
          className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-white/80 backdrop-blur shadow-sm flex items-center justify-center text-stone-400 hover:text-orange-500 hover:shadow-md transition-all active:scale-90 z-20"
          title="予定を入れる"
        >
          <Calendar size={18}/>
        </button>

        <div className="absolute bottom-1 right-3 text-9xl font-black text-stone-300 opacity-50 select-none pointer-events-none">{table.name}</div>
        
        <div className="flex flex-col items-center gap-2 z-10 pointer-events-none">
           <div className="w-16 h-16 rounded-full bg-white/60 border-2 border-stone-100 flex items-center justify-center group-hover:scale-110 group-hover:bg-white group-hover:border-orange-200 transition-all shadow-inner">
             <Plus size={36} className="text-stone-300 group-hover:text-orange-500" />
           </div>
           <span className="text-sm font-black text-stone-400 tracking-wider uppercase mt-1 group-hover:text-stone-600 transition-colors">受付開始</span>
           <span className="text-[10px] font-bold text-stone-300">定員: {table.capacity}名</span>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {appSettings.announcement && (
        <div className="bg-red-50 text-red-600 px-4 py-2 mb-4 rounded-xl flex items-center gap-2 border border-red-100 shadow-sm overflow-hidden shrink-0">
          <AlertCircle size={16} className="shrink-0 animate-pulse" />
          <div className="flex-1 overflow-hidden relative h-5">
            <div className="animate-[marquee_10s_linear_infinite] whitespace-nowrap absolute">{appSettings.announcement}</div>
          </div>
        </div>
      )}

      {!selectedSessionId ? (
        <>
          <div className="flex justify-between items-center mb-6 shrink-0">
            <div>
              <h2 className="text-2xl font-bold text-cafe-text">フロア状況</h2>
              <p className="text-xs text-stone-500 mt-1">稼働率: {Math.round((activeSessions.length / (tables.length || 1)) * 100)}% ({activeSessions.length}/{tables.length})</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 content-start no-scrollbar">
            {tables.map(table => renderTable(table))}
          </div>
        </>
      ) : (
        <div className="h-full flex flex-col md:flex-row gap-4 overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden bg-white rounded-3xl shadow-sm border border-stone-100 order-2 md:order-1">
            <div className="p-4 border-b border-stone-100 flex gap-3"><button onClick={() => setSelectedSessionId(null)} className="md:hidden p-2 bg-stone-100 rounded-full"><ArrowLeft size={20} /></button><input type="text" placeholder="メニュー検索..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="flex-1 bg-stone-50 border-none rounded-xl py-2.5 px-4 text-sm font-bold" /></div>
            <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 lg:grid-cols-3 gap-4 content-start no-scrollbar">
              {filteredItems.map(item => (
                <button key={item.id} onClick={() => addToCart(item)} className="bg-white p-3 rounded-2xl shadow-sm border border-stone-50 text-left active:scale-95 transition-all"><img src={item.imageUrl} className="w-full h-24 object-cover rounded-xl mb-2"/><div className="text-xs font-bold truncate">{item.name}</div><div className="text-xs text-stone-400">¥{item.price}</div></button>
              ))}
            </div>
          </div>
          <div className="w-full md:w-80 bg-stone-800 text-white rounded-3xl p-4 flex flex-col order-1 md:order-2">
            <div className="mb-4 border-b border-stone-700 pb-2 flex justify-between items-center"><h2 className="font-bold truncate">{currentSession?.customerName} 様</h2><button onClick={() => { setEditSessionName(currentSession?.customerName || ''); setEditSessionGuests(currentSession?.guestCount || 1); setIsEditSessionModalOpen(true); }} className="p-2 hover:bg-stone-700 rounded-lg"><Edit2 size={16}/></button></div>
            <div className="flex-1 overflow-y-auto space-y-2 no-scrollbar">{cart.map(item => (<div key={item.id} className="flex justify-between items-center text-sm"><span>{item.name} ×{item.quantity}</span><div className="flex items-center gap-2"><button onClick={() => updateQuantity(item.id, -1)} className="p-1 bg-stone-700 rounded"><Minus size={12}/></button><button onClick={() => updateQuantity(item.id, 1)} className="p-1 bg-stone-700 rounded"><Plus size={12}/></button></div></div>))}</div>
            <div className="mt-4 pt-4 border-t border-stone-700 flex justify-between items-end font-bold"><span className="text-sm opacity-60">合計</span><span className="text-xl">¥{cartTotal.toLocaleString()}</span></div>
            <button onClick={handleCheckout} disabled={cart.length === 0} className="mt-4 w-full bg-orange-200 text-orange-900 py-3 rounded-xl font-bold disabled:opacity-50 transition-all">{showConfirmation ? "完了" : "会計完了"}</button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {isCheckInModalOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-[2rem] w-full max-w-sm p-6 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <h3 className="font-black text-lg mb-4 shrink-0">受付開始: {tables.find(t => t.id === targetTableId)?.name}</h3>
              <div className="flex-1 overflow-y-auto no-scrollbar space-y-4">
                 {todayReservations.length > 0 && (
                   <div className="bg-orange-50/50 p-4 rounded-2xl border border-orange-100">
                      <p className="text-[10px] font-black text-orange-500 uppercase mb-3 tracking-widest flex items-center gap-1"><Calendar size={12}/> 本日の予約から選択</p>
                      <div className="flex flex-col gap-2">
                         {todayReservations.map(res => (
                            <button 
                              key={res.id}
                              type="button"
                              onClick={() => { setNewCustomerName(res.name); setNewGuests(res.guests); setNewNotes(res.notes); }}
                              className="w-full text-left bg-white p-3 rounded-xl border border-orange-100 hover:border-orange-300 transition-all active:scale-95 shadow-sm"
                            >
                               <div className="font-black text-sm">{res.name} 様</div>
                               <div className="flex justify-between text-[10px] text-stone-400 font-bold mt-1">
                                  <span>{res.time}〜 ({res.guests}名)</span>
                                  {res.tableId === targetTableId && <span className="text-orange-500">指定テーブル</span>}
                               </div>
                            </button>
                         ))}
                      </div>
                   </div>
                 )}
                 <form onSubmit={handleCheckInSubmit} className="space-y-4">
                    <div className="space-y-1">
                       <label className="text-[10px] font-black text-stone-400 ml-1 uppercase">お名前</label>
                       <input required className="w-full bg-stone-50 rounded-xl p-4 text-sm font-bold border-none focus:ring-2 focus:ring-orange-100" placeholder="名前" value={newCustomerName} onChange={e => setNewCustomerName(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                         <label className="text-[10px] font-black text-stone-400 ml-1 uppercase">人数</label>
                         <SwipeNumberInput value={newGuests} onChange={setNewGuests} min={1} unit="名" />
                      </div>
                      <div className="space-y-1">
                         <label className="text-[10px] font-black text-stone-400 ml-1 uppercase">メモ</label>
                         <textarea className="bg-stone-50 rounded-xl p-3 text-xs font-bold resize-none h-[80px] w-full border-none focus:ring-2 focus:ring-orange-100" placeholder="アレルギーなど..." value={newNotes} onChange={e => setNewNotes(e.target.value)} />
                      </div>
                    </div>
                 </form>
              </div>
              <div className="flex gap-2 pt-6 shrink-0 border-t border-stone-50 mt-4">
                <button type="button" onClick={() => setIsCheckInModalOpen(false)} className="flex-1 py-3 font-bold text-stone-400 hover:bg-stone-50 rounded-xl transition-colors">閉じる</button>
                <button type="button" onClick={handleCheckInSubmit} className="flex-1 bg-stone-800 text-white py-3 rounded-xl font-bold shadow-lg active:scale-95 transition-all">受付開始</button>
              </div>
            </motion.div>
          </div>
        )}
        
        {isReservationModalOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-[2rem] w-full max-w-md p-6 shadow-2xl overflow-y-auto no-scrollbar max-h-[95vh]">
              <h3 className="font-black text-lg mb-4">簡易予約: {tables.find(t => t.id === targetTableId)?.name}</h3>
              <form onSubmit={handleReservationSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-stone-400 ml-1 uppercase">お名前</label>
                  <input required className="w-full bg-stone-50 rounded-xl p-4 text-sm font-bold border-none" placeholder="名前" value={newCustomerName} onChange={e => setNewCustomerName(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-stone-400 ml-1 uppercase">予約経路</label>
                  <div className="grid grid-cols-4 gap-2">
                    {['instagram', 'line', 'phone', 'store'].map((s: any) => {
                      const label = SOURCE_LABELS[s as ReservationSource];
                      const isSelected = newSource === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setNewSource(s)}
                          className={`flex flex-col items-center justify-center py-2 rounded-xl border transition-all ${
                            isSelected 
                              ? 'bg-stone-800 text-white border-stone-800 shadow-md scale-105' 
                              : 'bg-stone-50 border-stone-100 text-stone-400 hover:border-stone-200'
                          }`}
                        >
                          <SourceIcon source={s} />
                          <span className="text-[10px] font-bold mt-1">{label.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-stone-400 ml-1 uppercase">予約日</label>
                  <input type="date" required className="w-full bg-stone-50 rounded-xl p-4 text-sm font-bold border-none" value={reservationDate} onChange={e => setReservationDate(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-stone-400 ml-1 uppercase block text-center">来店時間</label>
                  <div className="flex gap-2">
                    <SwipeNumberInput label="時" value={reservationHour} onChange={setReservationHour} min={0} max={23} unit="時" />
                    <SwipeNumberInput label="分" value={reservationMinute} onChange={setReservationMinute} min={0} max={59} format={v=>v.toString().padStart(2,'0')} unit="分" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-stone-400 ml-1 uppercase">人数</label>
                    <SwipeNumberInput value={newGuests} onChange={setNewGuests} min={1} unit="名" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-stone-400 ml-1 uppercase">メモ</label>
                    <textarea className="bg-stone-50 rounded-xl p-3 text-xs font-bold resize-none h-[72px] w-full border-none" placeholder="メモ" value={newNotes} onChange={e => setNewNotes(e.target.value)} />
                  </div>
                </div>
                <div className="flex gap-2 pt-4 shrink-0 border-t border-stone-50 mt-2">
                  <button type="button" onClick={() => setIsReservationModalOpen(false)} className="flex-1 py-3 font-bold text-stone-400 hover:bg-stone-50 rounded-xl transition-colors">閉じる</button>
                  <button type="submit" className="flex-1 bg-stone-800 text-white py-3 rounded-xl font-bold shadow-lg active:scale-95">予約</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
        
        {isEditSessionModalOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-[2rem] w-full max-w-sm p-6 shadow-2xl">
              <h3 className="font-bold text-lg mb-4">伝票情報の変更</h3>
              <form onSubmit={e => { e.preventDefault(); if(selectedSessionId) onUpdateSessionInfo(selectedSessionId, editSessionName, editSessionGuests); setIsEditSessionModalOpen(false); }} className="space-y-4">
                <input required className="w-full bg-stone-50 rounded-xl p-4 text-sm font-bold border-none" placeholder="名前" value={editSessionName} onChange={e => setEditSessionName(e.target.value)} />
                <SwipeNumberInput label="人数" value={editSessionGuests} onChange={setEditSessionGuests} min={1} unit="名" />
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => { if(confirm('セッションを削除しますか？')){ onDeleteSession(selectedSessionId!); setSelectedSessionId(null); setIsEditSessionModalOpen(false); } }} className="px-4 py-3 rounded-xl bg-red-50 text-red-500 font-bold"><Trash2 size={20}/></button>
                  <button type="button" onClick={() => setIsEditSessionModalOpen(false)} className="flex-1 py-3 font-bold text-stone-400">閉じる</button>
                  <button type="submit" className="flex-1 bg-stone-800 text-white py-3 rounded-xl font-bold shadow-lg">保存</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
